import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDateOnly } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/screened-out")({
  component: ScreenedOut,
});

const ALL = "all";

type Candidate = Output<typeof orpcClient.workforce.hiring.screenedOut>[number];

const COLUMNS: ColumnDef<Candidate>[] = [
  {
    id: "candidate",
    header: "Candidate",
    enableHiding: false,
    accessorFn: (candidate) =>
      [candidate.firstName, candidate.lastName, candidate.email, candidate.phone]
        .filter(Boolean)
        .join(" "),
    cell: ({ row: { original: candidate } }) => (
      <PersonCell
        name={`${candidate.firstName} ${candidate.lastName}`}
        meta={[candidate.email, candidate.phone]}
      />
    ),
  },
  {
    id: "job",
    header: "Job",
    accessorFn: (candidate) => candidate.jobTitle ?? "-",
    meta: { filter: "select", clamp: "md", className: "text-muted-foreground" },
  },
  {
    id: "screened",
    header: "Screened on",
    accessorFn: (candidate) => new Date(candidate.createdAt).getTime(),
    cell: ({ row: { original: candidate } }) => formatDateOnly(candidate.createdAt),
    meta: { nowrap: true },
  },
];

/** The people a job's screening questions turned away, so a building can see who it never spoke to.
 * No account and no application exists for any of them. */
function ScreenedOut() {
  const { facilityId } = useFacility();

  const jobs = useQuery(orpc.workforce.hiring.listJobs.queryOptions({ input: { facilityId } }));
  const screenedOut = useQuery(orpc.workforce.hiring.screenedOut.queryOptions({ input: {} }));

  const pending = screenedOut.isPending || jobs.isPending;
  const failed = [screenedOut, jobs].find((query) => query.isError);
  // `screenedOut` scopes by who is asking, not by the facility switcher, so the
  // building's own jobs are what narrow it.
  const jobIds = new Set((jobs.data ?? []).map((job) => job.id));
  const candidates = (screenedOut.data ?? []).filter(
    (candidate) => facilityId === ALL || jobIds.has(candidate.jobId),
  );

  return (
    <>
      <PageHeader
        title="Screened out"
        count={pending || failed ? undefined : candidates.length}
        meta={
          pending || failed
            ? undefined
            : candidates.length > 0
              ? "Did not pass screening. No account or application was created for them."
              : "Candidates who did not pass the screening questions."
        }
      />

      {pending ? (
        <Skeleton className="h-64 w-full" />
      ) : failed ? (
        <ReadFailed
          message="Could not load the screened-out candidates."
          error={failed.error}
          retrying={failed.isFetching}
          onRetry={() => void failed.refetch()}
        />
      ) : candidates.length === 0 ? (
        <EmptyState
          title="No screened-out candidates"
          description="Candidates who fail the screening questions appear here."
        />
      ) : (
        <DataTable
          data={candidates}
          columns={COLUMNS}
          noun="candidates"
          rowId={(candidate) => candidate.id}
          renderToolbar={(table) => (
            <DataTableToolbar table={table} searchPlaceholder="Search candidates…" />
          )}
        />
      )}
    </>
  );
}
